<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuranPlayer } from '@/composables/quran/useQuranPlayer';

/** Seekable progress through the current ayah, with elapsed and total time. */
defineProps({
  times: { type: Boolean, default: true },
});

const { t } = useI18n();
const player = useQuranPlayer();
const state = player.state;
const track = ref(null);
const dragging = ref(false);

const pct = computed(() => `${Math.round(player.progress.value * 1000) / 10}%`);
const time = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, '0')}`;

function seekAt(clientX) {
  const rect = track.value.getBoundingClientRect();
  const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  player.seekTo(fraction * state.duration);
}

function onDown(event) {
  dragging.value = true;
  seekAt(event.clientX);
  event.currentTarget.setPointerCapture(event.pointerId);
}
const onMove = (event) => dragging.value && seekAt(event.clientX);
const onUp = () => (dragging.value = false);
</script>

<template>
  <div class="flex items-center gap-3 w-full">
    <span v-if="times" class="w-10 text-right text-[11px] font-semibold tabular-nums text-white/50">{{ time(state.currentTime) }}</span>
    <div
      ref="track"
      class="group relative flex-1 h-5 flex items-center cursor-pointer touch-none"
      role="slider"
      tabindex="0"
      :aria-label="t('quran.player.seek')"
      aria-valuemin="0"
      :aria-valuemax="Math.round(state.duration)"
      :aria-valuenow="Math.round(state.currentTime)"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @keydown.right.prevent="player.seekTo(state.currentTime + 5)"
      @keydown.left.prevent="player.seekTo(state.currentTime - 5)"
    >
      <div class="relative w-full h-1 group-hover:h-1.5 transition-all rounded-full bg-white/15 overflow-hidden">
        <div class="absolute inset-y-0 left-0 rounded-full bg-deen-gold" :style="{ width: pct }" />
      </div>
      <div
        class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-md transition-transform"
        :class="dragging ? 'scale-100' : 'scale-0 group-hover:scale-100'"
        :style="{ left: pct }"
      />
    </div>
    <span v-if="times" class="w-10 text-[11px] font-semibold tabular-nums text-white/50">{{ time(state.duration) }}</span>
  </div>
</template>
